/**
 * price-range-slider.js
 * Dual-handle price range slider custom element.
 *
 * Works with:
 * - Mobile price-facet-component (AJAX): dispatches a change event on the min input,
 *   which bubbles and is caught by the on:change="/updatePriceFilterAndResults" handler.
 * - Desktop sidebar form (full-page submit): submits the closest <form> on settle.
 *
 * Required attributes:
 *   data-range-max         — max price in major currency units (integer), e.g. "5000"
 *   data-min-input         — id of the min price <input>
 *   data-max-input         — id of the max price <input>
 *   data-currency-symbol   — optional currency symbol appended to tooltip, e.g. "Kč"
 *
 * Required children:
 *   .price-range-slider__track
 *   .price-range-slider__fill
 *   .price-range-slider__thumb--min
 *   .price-range-slider__thumb--max
 */
class PriceRangeSlider extends HTMLElement {
  connectedCallback() {
    this._rangeMax = parseInt(this.dataset.rangeMax, 10) || 0;
    if (this._rangeMax <= 0) return;

    this._thumbMin = this.querySelector('.price-range-slider__thumb--min');
    this._thumbMax = this.querySelector('.price-range-slider__thumb--max');
    this._fill = this.querySelector('.price-range-slider__fill');

    if (!this._thumbMin || !this._thumbMax || !this._fill) return;

    // Tooltip bubble — created in JS so no Liquid changes are needed.
    // Guard against double-creation after morph.
    if (!this._bubble) {
      this._bubble = document.createElement('span');
      this._bubble.className = 'price-range-slider__bubble';
      this.prepend(this._bubble);
    }

    // Event delegation on the custom element survives morph's in-place DOM updates.
    this.addEventListener('mousedown', this._onDragStart);
    this.addEventListener('touchstart', this._onDragStart, { passive: false });

    // Keep slider visuals in sync when user edits inputs via keyboard.
    this._onInputChange = () => this._syncFromInputValues();
    this._getMinInput()?.addEventListener('change', this._onInputChange);
    this._getMaxInput()?.addEventListener('change', this._onInputChange);
  }

  disconnectedCallback() {
    this.removeEventListener('mousedown', this._onDragStart);
    this.removeEventListener('touchstart', this._onDragStart);
    this._getMinInput()?.removeEventListener('change', this._onInputChange);
    this._getMaxInput()?.removeEventListener('change', this._onInputChange);
  }

  /** Always resolve inputs fresh to avoid stale references after morphs. */
  _getMinInput() { return document.getElementById(this.dataset.minInput ?? ''); }
  _getMaxInput() { return document.getElementById(this.dataset.maxInput ?? ''); }

  _toPct(val) { return (val / this._rangeMax) * 100; }
  _fromPct(pct) { return Math.round((pct / 100) * this._rangeMax); }

  _getMinPct() { return parseFloat(this._thumbMin.style.left) || 0; }
  _getMaxPct() { return parseFloat(this._thumbMax.style.left) || 100; }

  _setMinPct(pct) { this._thumbMin.style.left = pct + '%'; }
  _setMaxPct(pct) { this._thumbMax.style.left = pct + '%'; }

  _updateFill() {
    this._fill.style.left = this._getMinPct() + '%';
    this._fill.style.right = (100 - this._getMaxPct()) + '%';
  }

  /** Parse a locale-formatted price string to a float (major units). */
  _parsePrice(str) {
    if (!str || !str.trim()) return null;
    const n = parseFloat(str.replace(/\s/g, '').replace(',', '.'));
    return isNaN(n) ? null : n;
  }

  /**
   * Update bubble text and clamp its horizontal position within the slider so it
   * never overflows and causes a scrollbar.
   */
  _placeBubble(thumbPct, val) {
    const sym = this.dataset.currencySymbol;
    this._bubble.textContent = sym ? val + '\u00a0' + sym : String(val);

    const track = this.querySelector('.price-range-slider__track');
    const trackMarginLeft = parseFloat(getComputedStyle(track).marginLeft) || 0;
    const thumbCenterX = trackMarginLeft + (thumbPct / 100) * track.offsetWidth;
    const halfBubble = this._bubble.offsetWidth / 2;
    const sliderW = this.offsetWidth;
    this._bubble.style.left = Math.max(halfBubble, Math.min(sliderW - halfBubble, thumbCenterX)) + 'px';
    // Sit just above the track regardless of how much padding the slider has.
    this._bubble.style.top = (track.offsetTop - 18 - this._bubble.offsetHeight) + 'px';
  }

  /** Re-position thumbs when the user changes inputs via keyboard then triggers change. */
  _syncFromInputValues() {
    const minVal = this._parsePrice(this._getMinInput()?.value);
    const maxVal = this._parsePrice(this._getMaxInput()?.value);
    this._setMinPct(minVal != null && minVal > 0 ? this._toPct(minVal) : 0);
    this._setMaxPct(maxVal != null && maxVal < this._rangeMax ? this._toPct(maxVal) : 100);
    this._updateFill();
  }

  _onDragStart = (e) => {
    const thumb = e.target.closest('.price-range-slider__thumb');
    if (!thumb) return;
    e.preventDefault();

    const isMin = thumb.classList.contains('price-range-slider__thumb--min');
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const track = this.querySelector('.price-range-slider__track');
    const trackWidth = track.getBoundingClientRect().width;
    const startX = clientX;
    const startPct = isMin ? this._getMinPct() : this._getMaxPct();

    // Show bubble at the drag-start value.
    const startVal = this._fromPct(startPct);
    this._placeBubble(startPct, isMin ? (startVal <= 0 ? 0 : startVal) : (startVal >= this._rangeMax ? this._rangeMax : startVal));
    this._bubble.classList.add('price-range-slider__bubble--visible');

    const onMove = (e) => {
      // Prevent browser from claiming a scroll gesture and cancelling the touch.
      e.preventDefault();

      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const deltaPct = ((cx - startX) / trackWidth) * 100;
      let newPct = Math.max(0, Math.min(100, startPct + deltaPct));

      // Re-fetch inputs each move to avoid stale references after morphs.
      const minInput = this._getMinInput();
      const maxInput = this._getMaxInput();

      if (isMin) {
        newPct = Math.min(newPct, this._getMaxPct());
        this._setMinPct(newPct);
        const val = this._fromPct(newPct);
        if (minInput) minInput.value = val <= 0 ? '' : val;
        this._placeBubble(newPct, val <= 0 ? 0 : val);
      } else {
        newPct = Math.max(newPct, this._getMinPct());
        this._setMaxPct(newPct);
        const val = this._fromPct(newPct);
        if (maxInput) maxInput.value = val >= this._rangeMax ? '' : val;
        this._placeBubble(newPct, val >= this._rangeMax ? this._rangeMax : val);
      }

      this._updateFill();
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
      this._bubble.classList.remove('price-range-slider__bubble--visible');
    };

    const onEnd = () => { cleanup(); this._settle(); };

    // On touchcancel the gesture was interrupted — reset visuals, don't apply filter.
    const onCancel = () => {
      cleanup();
      if (isMin) this._setMinPct(startPct);
      else this._setMaxPct(startPct);
      this._updateFill();
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onCancel);
  };

  _settle() {
    // Mobile: dispatch a change event on the min input so it bubbles to
    // price-facet-component and triggers on:change="/updatePriceFilterAndResults".
    const priceFacet = this.closest('price-facet-component');
    if (priceFacet) {
      const minInput = this._getMinInput();
      if (minInput) {
        minInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
    // Desktop sidebar: submit the closest form.
    const form = this.closest('form');
    if (form) {
      typeof form.requestSubmit === 'function' ? form.requestSubmit() : form.submit();
    }
  }
}

if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}
